import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import ElliottFace from '../../../../components/ElliottFace';
import {
  gapOccurrences,
  gapSourceLabel,
  isGapClosed,
  offersUpdateLesson,
  visibleEpisodes,
  knowledgeRecommendationCopy,
  improveLessonCopy,
  askToUpdateLessonCopy,
  discussLearnerNames,
  discussWithLearnerCopy,
  isQuestionsOnly,
  responsePlainText,
  type Gap,
  type GapEpisode,
  type LessonContact,
  type RecommendationKey,
  type RecommendationStatus,
  type RecommendedCourse,
  type TeamGaps
} from './knowledgeGaps';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import InfoTip from '../../../../../../components/ui/InfoTip';
import { formatDate } from '../../../../../../utils/formatDate';
import { CapabilityPanelSkeleton, GapsListSkeleton, fadeInSx } from '../../../../../../components/ui/DashboardSkeletons';
import TeamCapabilityScore from './TeamCapabilityScore';
import { useNavigate } from 'react-router-dom';
import useCapabilityRelease from '../../../../../../hooks/useCapabilityRelease';
import { useAppSelector } from '../../../../../../redux/hooks';
import { capabilityReleaseMode } from '../../../../../../utils/capabilityRelease';
import CapabilityGapsList from './CapabilityGapsList';
import { useCapabilityLevels, useCapabilityPairs, useCapabilityTrend } from '../../../../../../queries/useCapability';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';
export type { TeamGaps };

// Tick / cross colours lifted from the Org Members report so the quiz rows read
// the same in both places.
const CORRECT = '#00972D';
const INCORRECT = '#FF6A55';

export const BODY = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
  lineHeight: '18px',
  color: '#B6B6B6'
};

const BRANDY = '#F79420';

// All three recommendation CTAs share this width so they read as a set.
const CTA_WIDTH = 168;

// Near-square corners throughout the panel — sharp rather than soft.
export const RADIUS = '2px';

// Width of this panel. The dashboard reuses it for the gamification hub above,
// so the hub's right edge lands on the divider between the two panels.
export const GAPS_PANEL_WIDTH = '30%';
// How many capabilities the phone lists before Show all.
const PHONE_CAPABILITIES = 5;

/**
 * One of the blocks inside an expanded gap — My Reports and My Recommendations.
 * Each is a bordered panel with a brandy title bar, so they read as distinct
 * sections rather than one run of text.
 */
export const GapSection: React.FC<{
  title: string;
  elliott?: boolean;
  /** Optional badge on the right of the title bar. */
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, elliott = false, right, children }) => (
  <Box
    sx={{
      flexShrink: 0,
      border: '1px solid #2A2A2A',
      borderRadius: RADIUS,
      overflow: 'hidden',
      bgcolor: '#121212'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        px: '10px',
        py: '7px',
        bgcolor: 'rgba(247, 148, 32, 0.08)',
        borderBottom: '1px solid rgba(247, 148, 32, 0.25)'
      }}
    >
      {elliott && <ElliottFace size={16} />}
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: BRANDY
        }}
      >
        {title}
      </Typography>
      {right}
    </Box>
    <Box sx={{ p: '10px' }}>{children}</Box>
  </Box>
);

// Without the Capability release (utils/capabilityRelease.ts) each gap shows what it is worth.
/**
 * What closing this gap is worth on the Learning Gaps Closed score. Shown as a
 * prize while the gap is open and as banked once it closes — the point is to
 * make the connection between working the list and the number above it.
 */
const RewardChip: React.FC<{ pct: number; earned: boolean }> = ({ pct, earned }) => (
  <Tooltip
    title={
      earned
        ? 'Closing this gap added this to your Learning Gaps Closed score.'
        : 'Action or ignore every recommendation below to close this gap and add this to your Learning Gaps Closed score.'
    }
  >
    <Typography
      sx={{
        ml: 'auto',
        flexShrink: 0,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        borderRadius: RADIUS,
        px: '5px',
        py: '1px',
        cursor: 'default',
        color: earned ? CORRECT : BRANDY,
        border: `1px solid ${earned ? CORRECT : BRANDY}`,
        bgcolor: earned ? 'rgba(0, 151, 45, 0.12)' : 'transparent'
      }}
    >
      +{pct}% {earned ? 'EARNED' : 'WHEN CLOSED'}
    </Typography>
  </Tooltip>
);

/**
 * A collapsed row that opens against a brandy left rule. Shared by My Reports
 * and My Recommendations so both sections read identically — same type, same
 * hairlines, same disclosure.
 */
export const CollapsibleRow: React.FC<{
  label: string;
  right?: React.ReactNode;
  disabled?: boolean;
  dimmed?: boolean;
  strike?: boolean;
  children?: React.ReactNode;
}> = ({ label, right, disabled = false, dimmed = false, strike = false, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Hairline between rows so the section reads as a table.
        '&:not(:last-of-type)': { borderBottom: '1px solid #1F1F1F' }
      }}
    >
      <Box
        onClick={() => !disabled && setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          py: '7px',
          cursor: disabled ? 'default' : 'pointer',
          opacity: dimmed ? 0.6 : 1,
          '&:hover': disabled ? {} : { '& .gap-row-label': { color: BRANDY } }
        }}
      >
        <Typography
          className="gap-row-label"
          sx={{
            ...BODY,
            flex: 1,
            color: '#FDFDFD',
            textDecoration: strike ? 'line-through' : 'none',
            transition: 'color 0.15s'
          }}
        >
          {label}
        </Typography>
        {right}
        <Box sx={{ width: 16, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          {!disabled &&
            (open ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: BRANDY }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: '#868686' }} />
            ))}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            ml: '2px',
            pl: '10px',
            borderLeft: `2px solid ${BRANDY}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            py: '8px',
            mb: '6px'
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

/** "Failed Live Scenarios  6" style row inside My Reports. */
export const ReportRow: React.FC<{ label: string; count: number | null; children?: React.ReactNode }> = ({
  label,
  count,
  children
}) => (
  <CollapsibleRow
    label={label}
    disabled={count === null || count === 0}
    dimmed={count === null}
    right={
      count === null ? (
        <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: '#868686' }}>
          COMING SOON
        </Typography>
      ) : (
        // The figure is the point of the row — brandy and oversized.
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1,
            color: count > 0 ? BRANDY : '#565656',
            minWidth: 16,
            textAlign: 'right'
          }}
        >
          {count}
        </Typography>
      )
    }
  >
    {children}
  </CollapsibleRow>
);

const SINCE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Episode dates arrive as unix seconds; on screen they read "Aug 27, 2026".
const formatSince = (openedAt: number): string => {
  if (!openedAt) return '';
  return formatDate(new Date(openedAt * 1000));
};

// "Last looked" hint for the refresh button: "Aug 27 '26 14:05". Its own
// format, kept as it was because it only appears in the refresh tooltip.
const formatComputedAt = (ts: number): string => {
  const d = new Date(ts * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${SINCE_MONTHS[d.getMonth()]} ${d.getDate()} '${String(d.getFullYear()).slice(-2)} ${hh}:${mm}`;
};

/**
 * Date heading above an earlier episode. The current episode has no heading (it
 * is the default view); earlier episodes, folded inside the Reports /
 * Recommendations group, are identified by their date alone, formatted
 * "MMM DD 'YY". Rendered muted; the brandy path is kept for any future use.
 */
const EpisodeHeading: React.FC<{ label: string; since: number; current: boolean; first: boolean }> = ({
  label,
  since,
  current,
  first
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: first ? 0 : '14px', pb: '4px' }}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: current ? BRANDY : '#868686'
      }}
    >
      {label || formatSince(since)}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#2A2A2A' }} />
  </Box>
);

/**
 * Earlier episodes, folded away behind one collapsed header. The current
 * episode always stands alone above it; everything in here is history, so the
 * whole group is muted and its actions locked.
 */
const PreviousGroup: React.FC<{ label: string; count: number; children: React.ReactNode }> = ({
  label,
  count,
  children
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: '14px' }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pb: '4px',
          cursor: 'pointer',
          '&:hover .prev-label': { color: '#B6B6B6' }
        }}
      >
        <Typography
          className="prev-label"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#868686',
            transition: 'color 0.15s'
          }}
        >
          {label} ({count})
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#2A2A2A' }} />
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: '#868686' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: '#868686' }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

/**
 * Elliott's reply to a learner's question, folded behind "View Response" so a
 * long answer doesn't bury the questions around it.
 */
export const ElliottResponse: React.FC<{ response: string }> = ({ response }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          border: 'none',
          bgcolor: 'transparent',
          p: 0,
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          color: open ? BRANDY : '#868686',
          cursor: 'pointer',
          '&:hover': { color: BRANDY }
        }}
      >
        View Response
        {open ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', gap: '6px', pl: '8px', borderLeft: '2px solid #2A2A2A' }}>
          <ElliottFace size={14} />
          <Typography sx={{ ...BODY, color: '#B6B6B6', whiteSpace: 'pre-line' }}>
            {responsePlainText(response)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

/** A learner's answer with only the mark carrying colour. */
export const MarkedAnswer: React.FC<{ correct: boolean; children: React.ReactNode }> = ({ correct, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
    <Typography sx={{ flexShrink: 0, width: 12, fontSize: 12, fontWeight: 700, color: correct ? CORRECT : INCORRECT }}>
      {correct ? '✓' : '✗'}
    </Typography>
    <Typography sx={{ ...BODY, color: '#868686' }}>{children}</Typography>
  </Box>
);

/**
 * A row inside My Recommendations — identical treatment to a My Reports row, but
 * the figure on the right is replaced by a Done checkbox. Each recommendation
 * is a single action, so a count would only ever read "1".
 */
export const RecommendationCard: React.FC<{
  label: string;
  status?: RecommendationStatus;
  // Who set the tick — admins and managers share ticks, so each sees who acted.
  actedBy?: string;
  onToggleDone?: () => void;
  onToggleIgnored?: () => void;
  // Not actionable yet — the checkbox shows a dash and there's no Ignore, so
  // the row can't be marked either way and doesn't hold the gap open.
  locked?: boolean;
  children: React.ReactNode;
  // Omit for a recommendation the admin acts on outside the platform.
  ctaLabel?: string;
  onCta?: () => void;
  ctaDisabled?: boolean;
  ctaTooltip?: string;
}> = ({
  label,
  status,
  actedBy,
  onToggleDone,
  onToggleIgnored,
  locked = false,
  children,
  ctaLabel,
  onCta,
  ctaDisabled = false,
  ctaTooltip
}) => {
  // "closed" with the Capability release, "done" before it (utils/capabilityRelease.ts).
  const doneWord = useCapabilityRelease() ? 'closed' : 'done';
  const done = status === 'done';
  const ignored = status === 'ignored';
  const cta = (
    <DosenButton
      tone="cta"
      sizeVariant="xs"
      disabled={ctaDisabled}
      onClick={onCta}
      sx={{
        alignSelf: 'flex-start',
        // Fixed width so the recommendation CTAs line up as a set.
        width: CTA_WIDTH,
        py: '7px',
        // "Message <first name>" can outrun the fixed width.
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        // Phone: the full width, and the whole label.
        [MOBILE_SX]: { width: '100%', whiteSpace: 'normal', overflow: 'visible' }
      }}
    >
      {ctaLabel}
    </DosenButton>
  );
  // Subtle, always shown once a tick is set: whoever set it, since an admin
  // and a manager work the same gap from their own dashboards.
  const actor = status && actedBy && !locked && (
    <Typography
      sx={{
        fontSize: 10,
        color: '#868686',
        whiteSpace: 'nowrap',
        maxWidth: 110,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' }
      }}
    >
      {actedBy}
    </Typography>
  );
  return (
    <CollapsibleRow
      label={label}
      dimmed={ignored}
      strike={ignored}
      right={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {actor}
          {locked ? (
          // Greyed with a dash — there's nothing to tick until it ships.
          <Tooltip title="Coming soon — nothing to mark yet">
            <Box
              sx={{
                width: 17,
                height: 17,
                flexShrink: 0,
                borderRadius: RADIUS,
                border: '1.5px solid #3A3A3A',
                bgcolor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'default'
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#565656', lineHeight: 1 }}>–</Typography>
            </Box>
          </Tooltip>
        ) : ignored ? (
          <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: '#868686' }}>
            IGNORED
          </Typography>
        ) : (
          <Tooltip title={done ? `Mark as not ${doneWord}` : `Mark as ${doneWord}`}>
            <Box
              component="span"
              role="checkbox"
              aria-checked={done}
              aria-label={`Mark "${label}" as ${doneWord}`}
              // Stop the click reaching the row, or ticking it would also
              // expand or collapse the section underneath.
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onToggleDone?.();
              }}
              sx={{
                width: 17,
                height: 17,
                flexShrink: 0,
                borderRadius: RADIUS,
                border: `1.5px solid ${done ? CORRECT : '#565656'}`,
                bgcolor: done ? CORRECT : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': { borderColor: done ? CORRECT : '#868686' }
              }}
            >
              {done && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#FDFDFD', lineHeight: 1 }}>✓</Typography>
              )}
            </Box>
          </Tooltip>
          )}
        </Box>
      }
    >
      {children}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        {ctaLabel &&
          (ctaTooltip ? (
            <Tooltip title={ctaTooltip}>
              <span style={{ cursor: 'default' }}>{cta}</span>
            </Tooltip>
          ) : (
            cta
          ))}
        {!locked && (
          <Box
            component="button"
            type="button"
            onClick={onToggleIgnored}
            sx={{
              border: 'none',
              bgcolor: 'transparent',
              p: 0,
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: '#868686',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: '#FDFDFD' }
            }}
          >
            {ignored ? 'Unignore' : 'Ignore'}
          </Box>
        )}
      </Box>
    </CollapsibleRow>
  );
};

// Label above its value — the panel is narrow, so a side-by-side label column
// left the values on a stub of a line.
export const CourseField: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#868686'
        }}
      >
        {label}
      </Typography>
      {hint && <InfoTip title={hint} />}
    </Box>
    <Typography sx={{ ...BODY, color: '#FDFDFD' }}>{value || 'Not set'}</Typography>
  </Box>
);

/**
 * Severity scale shared by the per-team gap count and the per-gap occurrence
 * count: under 3 green, 3–5 amber, above 5 red. (The spec's bands overlap at 5
 * — "3-5 yellow" and "5+ red" — so 5 is treated as the top of the amber band.)
 */
const severityColor = (count: number): string => {
  // Brand-heat scale (reuses hexes already in the product): success green when
  // low, brandy when it needs watching, ember (the hot end of the brand
  // gradient) when high. Replaces the off-brand amber/salmon.
  if (count < 3) return '#00972D'; // success green — low
  if (count <= 5) return '#F79420'; // brandy — watch
  return '#F15A26'; // ember — high
};

/**
 * Brandy inline link inside a sentence — the panel's empty states end in an
 * action, so the admin can fix the setup from here instead of hunting for it.
 */
const InlineLink: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      border: 'none',
      background: 'none',
      p: 0,
      font: 'inherit',
      color: BRANDY,
      fontWeight: 600,
      cursor: onClick ? 'pointer' : 'default',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      '&:hover': { color: '#FFB259' }
    }}
  >
    {children}
  </Box>
);

export interface GapsRisksPanelProps {
  /** Inside AdminInsightsPanel, which owns the frame — render without one. */
  embedded?: boolean;
  /** Replaces the "Learning Gaps" title in the header (the Gaps / Tier toggle). */
  headerLeft?: React.ReactNode;
  // Every Team the admin may filter by, and the ones currently selected.
  teamOptions: { id: string; name: string }[];
  selectedTeamIds: string[];
  onTeamsChange: (ids: string[]) => void;
  // Gaps for each selected team, in selection order.
  /** Gaps for the teams currently SELECTED — what the panel renders. */
  teams: TeamGaps[];
  /**
   * Gaps across EVERY team this admin administers. The Gaps & Risks Closed
   * score is measured over their whole remit, not over whatever they happen to
   * have filtered to — otherwise the score moves when the filter does, which
   * makes it a view rather than a score. Defaults to `teams`.
   */
  scoringTeams?: TeamGaps[];
  // Publish a recommended course — opens Add New Program prefilled.
  onPublishCourse: (course: RecommendedCourse) => void;
  onOpenKnowledgeHub?: () => void;
  // Improve Lesson Content — opens the originating program in the editor.
  onImproveLesson?: (gap: Gap) => void;
  // Message whoever can edit a lesson the viewer can't (Update Lesson).
  onMessageContact?: (contact: LessonContact) => void;
  // Reports gap closure up so the gamification hub can score it. Fired from an
  // effect on the counts, not on every render.
  onGapCountsChange?: (counts: { closed: number; total: number }) => void;
  // Persist a done / ignored tick (null clears it) — the BE owns the state.
  onSetStatus: (
    gapId: string,
    episodeId: string,
    key: Exclude<RecommendationKey, 'assessment'>,
    next: RecommendationStatus | null
  ) => void;
  loading?: boolean;
  error?: string | null;
  // Loaded, but the org has no attribute list marked as Teams.
  teamsListMissing?: boolean;
  // When the BE last ran the engine for this org (unix seconds).
  lastComputedAt?: number | null;
  refreshing?: boolean;
  // Run the engine now.
  onRefresh?: () => void;
  // Organization Settings → Manage Attributes, where the Teams list is marked.
  // Offered in the panel's own empty state so an unconfigured org has the fix
  // one click away rather than a sentence telling it where to look.
  onOpenOrgSettings?: () => void;
}

/**
 * Gaps & Risks — the left third of the Admin Learning Framework dashboard.
 *
 * The selected Team heads the panel with its gap count; each gap expands to
 * My Report and My Recommendation. Recommendations are what feed the
 * Recommended Course cards into the framework on the right.
 */
/**
 * Teams multi-select — shared by the Gaps and Tier views of the admin panel, so
 * one selection filters both. At least one team is always selected.
 */
export const TeamsSelect: React.FC<{
  teamOptions: { id: string; name: string }[];
  selectedTeamIds: string[];
  onTeamsChange: (ids: string[]) => void;
}> = ({ teamOptions, selectedTeamIds, onTeamsChange }) => {
  // Icon-only trigger so the panel's view titles keep the header; the menu is
  // the same checkbox list as before.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const filtered = selectedTeamIds.length < teamOptions.length;
  const summary =
    selectedTeamIds.length === teamOptions.length
      ? 'All teams'
      : selectedTeamIds.length === 1
        ? teamOptions.find((t) => t.id === selectedTeamIds[0])?.name ?? '1 team'
        : `${selectedTeamIds.length} teams`;
  const toggleTeam = (id: string) => {
    const next = selectedTeamIds.includes(id) ? selectedTeamIds.filter((t) => t !== id) : [...selectedTeamIds, id];
    if (next.length > 0) onTeamsChange(next);
  };
  return (
    <>
      <Tooltip title={`Teams: ${summary}`}>
        <IconButton
          aria-label={`Filter teams (${summary})`}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 30,
            height: 30,
            p: 0,
            position: 'relative',
            color: filtered ? BRANDY : '#B6B6B6',
            '&:hover': { color: BRANDY },
            [MOBILE_SX]: { width: 36, height: 36 }
          }}
        >
          <FilterListIcon sx={{ fontSize: 19 }} />
          {/* Dot when narrowed to fewer than all teams. */}
          {filtered && (
            <Box
              sx={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', bgcolor: BRANDY }}
            />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: '#121212',
            // MUI Paper adds an elevation overlay in dark mode that lifts the
            // fill back to grey; turn it off so the near-black actually shows.
            backgroundImage: 'none',
            border: '1px solid #444444',
            maxHeight: 320,
            '& .MuiMenuItem-root': { color: '#B6B6B6', fontFamily: 'Inter, sans-serif', fontSize: 13, gap: '8px' }
          }
        }}
      >
    {teamOptions.map((team) => {
      const checked = selectedTeamIds.includes(team.id);
      const onlyOne = checked && selectedTeamIds.length === 1;
      return (
        <MenuItem
          key={team.id}
          disabled={onlyOne}
          onClick={() => toggleTeam(team.id)}
          sx={{ color: checked ? '#FDFDFD !important' : undefined }}
        >
          <Box
            sx={{
              width: 15,
              height: 15,
              flexShrink: 0,
              borderRadius: '3px',
              // Neutral checkbox with only the tick carrying brandy.
              border: `1.5px solid ${checked ? BRANDY : '#565656'}`,
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: BRANDY,
              lineHeight: 1
            }}
          >
            {checked ? '✓' : ''}
          </Box>
          {team.name}
        </MenuItem>
      );
    })}
      </Menu>
    </>
  );
};

const GapsRisksPanel: React.FC<GapsRisksPanelProps> = ({
  embedded = false,
  headerLeft,
  teamOptions,
  selectedTeamIds,
  onTeamsChange,
  teams,
  scoringTeams,
  onPublishCourse,
  onOpenKnowledgeHub,
  onImproveLesson,
  onMessageContact,
  onGapCountsChange,
  onSetStatus,
  loading = false,
  error = null,
  teamsListMissing = false,
  lastComputedAt = null,
  refreshing = false,
  onRefresh,
  onOpenOrgSettings
}) => {
  // One gap open at a time — opening another closes the last. Teams are
  // multi-open, but a gap is long enough that two at once is unreadable.
  const [expandedGap, setExpandedGap] = useState<string | null>(null);
  const toggle = (id: string) => setExpandedGap((prev) => (prev === id ? null : id));

  // Teams collapse by default.
  const [openTeams, setOpenTeams] = useState<string[]>([]);

  // Everything scored runs over the admin's whole remit, not the current
  // filter: an unselected team's closed gaps still count.
  const scored = scoringTeams ?? teams;
  // The Capability tab, or Learning Gaps as it was before the release (utils/capabilityRelease.ts).
  const capabilityRelease = useCapabilityRelease();

  // Capability Levels for the selected teams (every team selected is no filter). With none
  // to show yet, the team rows below stay.
  const levelTeams = selectedTeamIds.length > 0 && selectedTeamIds.length < teamOptions.length ? selectedTeamIds : null;
  const {
    data: levelRows,
    isLoading: levelsLoading,
    isPending: levelsPending
  } = useCapabilityLevels(levelTeams, capabilityRelease);
  const capabilityRows = capabilityRelease ? levelRows ?? [] : [];
  // The score card and its chart (TeamCapabilityScore asks the same queries; React Query shares them).
  const scoreTeams = teamOptions.filter((t) => selectedTeamIds.includes(t.id));
  const scoreTeamIds = scoreTeams.map((t) => t.id);
  const scoreEnabled = capabilityRelease && scoreTeamIds.length > 0;
  const { isPending: pairsPending } = useCapabilityPairs(scoreTeamIds, scoreEnabled);
  const { isPending: trendPending } = useCapabilityTrend(scoreTeamIds, scoreEnabled);
  // The Capability tab shows one placeholder until every section's data is in, then all of it at
  // once, instead of the score, the chart and the list landing one by one (and each team's
  // Capability Gaps standing in for the list while its levels load). A request that fails stops
  // waiting too, so the tab never hangs on one section.
  const seedingTeams = teamOptions.length > 0 && selectedTeamIds.length === 0;
  const capabilityFirstLoad =
    capabilityRelease &&
    ((loading && teams.length === 0) ||
      seedingTeams ||
      levelsPending ||
      (scoreEnabled && (pairsPending || trendPending)));
  // Admin mode (utils/capabilityRelease.ts) is the setup phase: with no role set up yet the section
  // says so and points to Capability Setup, instead of falling back to each team's Learning Gaps.
  const adminMode = useAppSelector(
    (state) => capabilityReleaseMode(state.orgDetails?.data as Parameters<typeof capabilityReleaseMode>[0]) === 'admin'
  );
  const navigate = useNavigate();
  const levelsEmptyInSetup = capabilityRelease && adminMode && !levelsLoading && capabilityRows.length === 0;
  // The phone opens on the weakest capabilities only (the rows come weakest first).
  const isMobileApp = useIsMobileApp();
  const [allCapabilities, setAllCapabilities] = useState(false);
  const phoneCapabilities = isMobileApp && !allCapabilities;

  // The admin's done / ignored ticks live on the episodes as the BE returned
  // them (the dashboard's data hook applies each tick optimistically, then
  // swaps in the BE's answer). A gap that reopens raises a new episode, and
  // the old one keeps its own ticks as history.
  const statusByEpisode = useMemo(() => {
    const map = new Map<string, GapEpisode['status']>();
    for (const team of scored) {
      for (const gap of team.gaps) {
        for (const episode of gap.episodes) map.set(episode.id, episode.status || {});
      }
    }
    return map;
  }, [scored]);
  const statusFor = (episodeId: string, key: RecommendationKey) => statusByEpisode.get(episodeId)?.[key];

  // Gap closure across every selected team, reported up to the dashboard.
  const allGaps = scored.flatMap((t) => t.gaps);
  const totalGaps = allGaps.length;
  const closedGaps = allGaps.filter((g) => isGapClosed(g, statusFor)).length;
  // What closing one more gap adds to the score (shown without the release).
  // Rounded to match the score itself, which is also rounded — so the two always agree on screen.
  const gapWorthPct = totalGaps > 0 ? Math.round(100 / totalGaps) : 0;

  // Report upward on the counts only, never on every render: the dashboard
  // passes an inline callback, so depending on its identity would loop.
  const reportCounts = useRef(onGapCountsChange);
  reportCounts.current = onGapCountsChange;
  useEffect(() => {
    reportCounts.current?.({ closed: closedGaps, total: totalGaps });
  }, [closedGaps, totalGaps]);
  // Clicking the current state again clears it — Done untoggles, Ignore
  // becomes Un-ignore. Skills assessments aren't actionable yet.
  const setStatus = (
    gapId: string,
    episodeId: string,
    key: Exclude<RecommendationKey, 'assessment'>,
    next: RecommendationStatus
  ) => onSetStatus(gapId, episodeId, key, statusFor(episodeId, key) === next ? null : next);
  const toggleTeam = (id: string) =>
    setOpenTeams((prev) => {
      if (!prev.includes(id)) return [...prev, id];
      // Collapsing a team takes its open gap down with it, so reopening the
      // team doesn't reveal a gap the admin left behind minutes ago.
      const team = teams.find((t) => t.id === id);
      if (team && expandedGap && team.gaps.some((g) => g.id === expandedGap)) setExpandedGap(null);
      return prev.filter((t) => t !== id);
    });

  return (
    <Box
      sx={{
        // Narrower than the framework — the gaps are the prompt, the framework
        // is the work. Embedded, the host panel owns the width and the frame.
        flex: embedded ? '1 1 auto' : `0 0 ${GAPS_PANEL_WIDTH}`,
        minWidth: 0,
        height: '100%',
        // minHeight + overflow hidden keep the scroll inside the body below,
        // rather than letting a long expanded gap stretch the whole column.
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(embedded
          ? {}
          : { bgcolor: '#1C1C1C', borderRight: '1px solid #2A2A2A', borderRadius: '8px 0 0 8px' })
      }}
    >
      {/* Panel header — same height, padding and bottom border as the Learning
          Framework header so the two underlines run as one line across. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '14px',
          py: '10px',
          minHeight: 56,
          borderBottom: '1px solid #2A2A2A',
          [MOBILE_SX]: { px: '12px', gap: '4px' }
        }}
      >
        {headerLeft ?? (
          <Typography
            sx={{ fontSize: 17, fontWeight: 700, color: '#FDFDFD', flexShrink: 0, textTransform: 'uppercase' }}
          >
            {capabilityRelease ? 'Capability' : 'Learning Gaps'}
          </Typography>
        )}

        {/* Pushes refresh + filter to the right edge. */}
        <Box sx={{ flex: 1 }} />

        {/* Run the engine now — the nightly job is the usual cadence. */}
        {onRefresh && (
          <Tooltip
            title={
              refreshing
                ? 'Looking for new gaps…'
                : `Look for new gaps now${lastComputedAt ? ` (last looked ${formatComputedAt(lastComputedAt)})` : ''}`
            }
          >
            <span>
              <IconButton
                aria-label="Look for new gaps now"
                onClick={onRefresh}
                disabled={refreshing || teamsListMissing}
                sx={{
                  width: 30,
                  height: 30,
                  p: 0,
                  color: '#B6B6B6',
                  '&:hover': { color: BRANDY },
                  [MOBILE_SX]: { width: 36, height: 36 }
                }}
              >
                {/* While looking, the icon stays and the button is disabled; the tooltip says it is looking. */}
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Team filter — rightmost, so it sits in the same spot in the Tier view
            (which has no refresh). Only once there are teams to choose. */}
        {teamOptions.length > 0 && (
          <TeamsSelect teamOptions={teamOptions} selectedTeamIds={selectedTeamIds} onTeamsChange={onTeamsChange} />
        )}
      </Box>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: '14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          [MOBILE_SX]: { p: '12px' },
          scrollbarWidth: 'thin',
          scrollbarColor: '#565656 transparent',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#565656', borderRadius: '4px' }
        }}
      >
        {capabilityFirstLoad ? (
          <CapabilityPanelSkeleton />
        ) : (
          <>
        {/* Team Capability score for the selected teams, above the team list. */}
        {capabilityRelease && <TeamCapabilityScore teams={scoreTeams} />}

        {/* Loading / error / not-set-up / nothing-selected states — the panel
            would otherwise render as a bare header, which reads as "nothing
            found" rather than "not ready yet". */}
        {loading && teams.length === 0 && <GapsListSkeleton />}
        {!loading && error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '4px' }}>
            <Typography sx={{ ...BODY, color: '#FDFDFD', fontWeight: 600 }}>
              {capabilityRelease ? 'Capability Gaps' : 'Learning Gaps'} could not be loaded. Refresh the page and try
              again.
            </Typography>
          </Box>
        )}
        {!loading && !error && teamsListMissing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '4px' }}>
            <Typography sx={{ ...BODY, color: '#FDFDFD', fontWeight: 600 }}>Add your teams first</Typography>
            <Typography sx={{ ...BODY }}>
              {capabilityRelease ? (
                <>
                  Capability is shown team by team. In{' '}
                  <InlineLink onClick={onOpenOrgSettings}>Organization Settings</InlineLink>, under Manage Attributes,
                  mark one list as Teams and give each person a team. Then refresh this page.
                </>
              ) : (
                <>
                  I look for gaps team by team, so this organization needs one attribute list marked as Teams, with a
                  value against each person. Mark it in{' '}
                  <InlineLink onClick={onOpenOrgSettings}>Organization Settings</InlineLink>, under Manage Attributes,
                  then refresh here.
                </>
              )}
            </Typography>
          </Box>
        )}
        {!loading && !error && !teamsListMissing && teams.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '4px' }}>
            <Typography sx={{ ...BODY, color: '#FDFDFD', fontWeight: 600 }}>
              {teamOptions.length === 0 ? 'No teams yet' : 'No team selected'}
            </Typography>
            <Typography sx={{ ...BODY }}>
              {teamOptions.length === 0 ? (
                <>
                  Add your teams in <InlineLink onClick={onOpenOrgSettings}>Organization Settings</InlineLink>, under
                  Manage Attributes, then refresh this page.
                </>
              ) : capabilityRelease ? (
                'Choose at least one team in the filter above to see their Capability.'
              ) : (
                'Pick at least one team from the filter above to see its gaps and risks.'
              )}
            </Typography>
          </Box>
        )}

        {/* Third section: the capabilities and their measures. Until capabilities
            are scored (production today) the rows below are each team's Learning
            Gaps, so the title says that instead. */}
        {capabilityRelease && (capabilityRows.length > 0 || teams.length > 0 || levelsEmptyInSetup) && (
          <Typography
            data-testid="capability-list-title"
            sx={{
              flexShrink: 0,
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              fontWeight: 600,
              color: '#FDFDFD'
            }}
          >
            {capabilityRows.length > 0 || levelsEmptyInSetup ? 'Capability' : 'Capability Gaps'}
          </Typography>
        )}
        {capabilityRows.length > 0 ? (
          <>
            <CapabilityGapsList
              rows={phoneCapabilities ? capabilityRows.slice(0, PHONE_CAPABILITIES) : capabilityRows}
            />
            {/* The phone lists the weakest few, so the board below is a short scroll away. */}
            {isMobileApp && capabilityRows.length > PHONE_CAPABILITIES && (
              <DosenButton
                tone="basic"
                sizeVariant="sm"
                onClick={() => setAllCapabilities((all) => !all)}
                sx={{ alignSelf: 'stretch', flexShrink: 0, minHeight: 40 }}
              >
                {allCapabilities ? 'Show fewer' : `Show all ${capabilityRows.length} capabilities`}
              </DosenButton>
            )}
          </>
        ) : levelsEmptyInSetup ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '4px' }}>
            <Typography sx={{ ...BODY, color: '#FDFDFD', fontWeight: 600 }}>
              <InlineLink onClick={() => navigate('/app/capability-setup')}>Add capabilities to your teams now.</InlineLink>
            </Typography>
            <Typography sx={{ ...BODY }}>
              Once done, they will appear here with capability scores assessed by Elliott.
            </Typography>
          </Box>
        ) : (
        teams.map((team) => {
          const teamOpen = openTeams.includes(team.id);
          // The counter tracks LIVE gaps — closing one takes it off the count.
          const liveGaps = team.gaps.filter((g) => !isGapClosed(g, statusFor)).length;
          return (
            <Box
              key={team.id}
              sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, ...fadeInSx }}
            >
              {/* Team row — styled as a pillar header (same border, radius and
                  uppercase 18px type) at the same height, so the first team
                  reads level with the pillar titles opposite: these pillars are
                  filtered to these teams. */}
              <Box
                onClick={() => toggleTeam(team.id)}
                sx={{
                  // Same slim brandy frame + faint tint as the pillar title boxes.
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid rgba(247, 148, 32, 0.45)',
                  bgcolor: 'rgba(247, 148, 32, 0.06)',
                  borderRadius: '4px',
                  pl: '12px',
                  pr: '10px',
                  py: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '8px',
                  flexShrink: 0,
                  minHeight: 36,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'rgba(247, 148, 32, 0.12)' }
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    color: '#FDFDFD',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 18,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible', lineHeight: 1.25 }
                  }}
                >
                  {team.name}
                </Typography>

                {/* Gap count, coloured by how much trouble the team is in. */}
                <Tooltip title={`${liveGaps} live ${liveGaps === 1 ? 'gap' : 'gaps'} of ${team.gaps.length} identified`}>
                  <Box
                    sx={{
                      // Heat chip: outline plus a faint tint in the severity
                      // colour, no glow — reads as a status pill rather than a
                      // lit block, and stays on-brand.
                      border: `1px solid ${severityColor(liveGaps)}`,
                      bgcolor: `${severityColor(liveGaps)}1F`,
                      borderRadius: RADIUS,
                      height: 22,
                      // Fixed width so every team's counter starts on the same
                      // vertical, however many gaps it carries — wide enough
                      // for a two-digit count without clipping the label.
                      width: 92,
                      px: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: severityColor(liveGaps),
                        lineHeight: 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                        {liveGaps} {liveGaps === 1 ? 'OPEN GAP' : 'OPEN GAPS'}
                    </Typography>
                  </Box>
                </Tooltip>

                <IconButton
                  aria-label={teamOpen ? `Collapse ${team.name}` : `Expand ${team.name}`}
                  sx={{ ml: 'auto', flexShrink: 0, width: 22, height: 22, p: 0, color: '#FDFDFD' }}
                >
                  {teamOpen ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Box>

              <Collapse in={teamOpen} unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {team.gaps.length === 0 && (
                    <Typography sx={{ ...BODY, color: '#868686' }}>
                        No gaps found for {team.name} yet. Elliott checks again every night. Use the refresh button
                        above to check now.
                    </Typography>
                  )}
                  {team.gaps.map((gap) => {
                    const open = expandedGap === gap.id;
                    const occurrences = gapOccurrences(gap);
                    const closed = isGapClosed(gap, statusFor);
                    // A later report and recommendation set is only surfaced
                    // once the previous one has been fully actioned.
                    // Current first, then previous newest-to-oldest — visibleEpisodes
                    // returns them oldest-first.
                    const shownEpisodes = [...visibleEpisodes(gap, statusFor)].reverse();
                    return (
            <Box
              key={gap.id}
              sx={{
                // flexShrink:0 is load-bearing — these are flex children of a
                // scrolling column, so without it an expanded gap compresses
                // its siblings instead of overflowing, and nothing scrolls.
                flexShrink: 0,
                border: '1px solid #444444',
                borderRadius: RADIUS,
                bgcolor: '#151515',
                overflow: 'hidden'
              }}
            >
              <Box
                onClick={() => toggle(gap.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: '12px',
                  py: '10px',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#1C1C1C' }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* The gap is the subject; the Report / Impacted Groups /
                      Recommendation labels inside it are subordinate, so this
                      outranks them rather than the other way round. */}
                  {/* One line only — long titles ellipsize, full title on hover. */}
                  <Tooltip
                    title={gap.title}
                                placement="bottom-start"
                    enterDelay={300}
                                componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                  >
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#FDFDFD',
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' }
                      }}
                    >
                      {gap.title}
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: 11, color: '#868686' }}>{gapSourceLabel(gap)}</Typography>
                </Box>
                {/* Occurrence count on the same severity scale as the team's
                    gap count — under 3 green, 3–5 amber, above 5 red. Once
                    every recommendation is done or ignored it flips to a
                    green GAP CLOSED. */}
                <Typography
                  sx={{
                    flexShrink: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: closed ? CORRECT : severityColor(occurrences),
                    border: `1px solid ${closed ? CORRECT : severityColor(occurrences)}`,
                    bgcolor: closed ? 'rgba(0, 151, 45, 0.12)' : 'transparent',
                    borderRadius: RADIUS,
                    px: '5px',
                    py: '1px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {closed
                    ? 'GAP CLOSED'
                    : `${occurrences} ${occurrences === 1 ? 'OCCURRENCE' : 'OCCURRENCES'}`}
                </Typography>
                <IconButton
                  aria-label={open ? `Collapse ${gap.title}` : `Expand ${gap.title}`}
                  sx={{ flexShrink: 0, width: 22, height: 22, p: 0, color: '#B6B6B6' }}
                >
                  {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>

              <Collapse in={open} unmountOnExit>
                <Box
                  sx={{
                    px: '12px',
                    pb: '12px',
                    pt: '10px',
                    borderTop: '1px solid #2A2A2A',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  {/* ---- My Report ---- */}
                  <GapSection title="Gaps" elliott>
                    {(() => {
                      const [current, ...previous] = shownEpisodes;
                      const renderReport = (episode: typeof current, past: boolean, i: number) => (

                        <Box
                          key={episode.id}
                          sx={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {/* Always numbered, from set 1 — the numbering is what
                              tells the admin a later set is a NEW surfacing. */}
                          {/* No heading on the current episode — it is the
                              default view. Earlier episodes keep a date heading
                              so a resurfacing reads as a new set of findings. */}
                          {past && (
                            <EpisodeHeading label="" since={episode.openedAt} current={false} first={i === 0} />
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <ReportRow label="Roleplay gaps" count={episode.scenarioFailures.length}>
                              {episode.scenarioFailures.map((f) => (
                                <Box key={f.id} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#FDFDFD' }}>
                                    {f.name}
                                  </Typography>
                                  <Typography sx={{ ...BODY }}>{f.assessment}</Typography>
                                </Box>
                              ))}
                            </ReportRow>

                                        <ReportRow label="Incorrect quiz answers" count={episode.quizFailures.length}>
                              {episode.quizFailures.map((f) => (
                                <Box key={f.id} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#FDFDFD' }}>
                                    {f.name}
                                  </Typography>
                                  {/* No assessment text here: the BE's quiz rows carry the lesson's
                                      live scenario verdict, which may be a pass. */}
                                  <Typography sx={{ ...BODY, color: '#868686', mt: '4px' }}>{f.question}</Typography>
                                  <MarkedAnswer correct={false}>{f.answer}</MarkedAnswer>
                                  <MarkedAnswer correct>{f.correctAnswer}</MarkedAnswer>
                                </Box>
                              ))}
                            </ReportRow>

                                        <ReportRow label="Elliott queries" count={episode.elliottQueries.length}>
                              {episode.elliottQueries.map((q) => (
                                <Box key={q.id} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#FDFDFD' }}>
                                      {q.userName}
                                    </Typography>
                                    {/* Only the miss is tagged — an answered question
                                        is the normal case, and its reply is a click
                                        away. The miss is what Add Knowledge is for. */}
                                    {q.status === 'Answer Not Found' && (
                                      <Typography
                                        sx={{
                                          fontSize: 9,
                                          fontWeight: 600,
                                          borderRadius: RADIUS,
                                          px: '4px',
                                          py: '1px',
                                          color: INCORRECT,
                                          border: `1px solid ${INCORRECT}`
                                        }}
                                      >
                                        {q.status}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography sx={{ ...BODY }}>“{q.question}”</Typography>
                                  {q.response && <ElliottResponse response={q.response} />}
                                </Box>
                              ))}
                            </ReportRow>

                                        <ReportRow label="Skill assessment gaps" count={null} />
                          </Box>
                        </Box>
                      );
                      return (
                        <>
                          {current && renderReport(current, false, 0)}
                          {previous.length > 0 && (
                                        <PreviousGroup label="Closed gaps" count={previous.length}>
                              {previous.map((episode, n) => renderReport(episode, true, n))}
                            </PreviousGroup>
                          )}
                        </>
                      );
                    })()}
                  </GapSection>

                  {/* ---- My Recommendation ---- */}
                  <GapSection
                    title="Recommendations"
                    elliott
                    right={
                      !capabilityRelease && gapWorthPct > 0 ? <RewardChip pct={gapWorthPct} earned={closed} /> : undefined
                    }
                  >
                    {(() => {
                      const [current, ...previous] = shownEpisodes;
                      const renderSet = (episode: typeof current, past: boolean, i: number) => {
                        // A round of questions to Elliott gets Add Knowledge,
                        // Publish Course (3+ questions only) and Discuss with
                        // Learner, in that order, and nothing else.
                        const questionsOnly = isQuestionsOnly(episode);
                        const unanswered = episode.elliottQueries.some((q) => q.status === 'Answer Not Found');
                        const course = episode.course;
                        const learners = discussLearnerNames(episode);

                        // Always first where learners failed the lesson — the
                        // only action that stops the gap forming again for
                        // people who have yet to take it. Someone who can't
                        // edit the lesson (a manager of another team) is
                        // pointed at whoever can, with a message to them.
                        const contact = gap.lessonContact;
                        const updateLesson = offersUpdateLesson(episode, gap.kind) && (
                            <RecommendationCard
                              key="improve"
                              label="Update Lesson"
                              status={statusFor(episode.id, 'improve')}
                              actedBy={episode.statusBy?.improve?.name}
                              onToggleDone={past ? undefined : () => setStatus(gap.id, episode.id, 'improve', 'done')}
                              onToggleIgnored={past ? undefined : () => setStatus(gap.id, episode.id, 'improve', 'ignored')}
                              {...(gap.canEditLesson
                                ? { ctaLabel: 'Open Lesson Editor', onCta: () => onImproveLesson?.(gap) }
                                : contact?.communicationId
                                  ? {
                                      ctaLabel: `Message ${contact.name.split(' ')[0] || contact.name}`,
                                      ctaTooltip: `Opens a chat with ${contact.name}`,
                                      onCta: () => onMessageContact?.(contact)
                                    }
                                  : {})}
                            >
                              <Typography sx={{ ...BODY }}>
                                {gap.canEditLesson ? improveLessonCopy() : askToUpdateLessonCopy(contact?.name || null)}
                              </Typography>
                            </RecommendationCard>
                        );

                        // The people behind this episode's failures — or who
                        // asked the questions — so the admin can follow up with
                        // each one directly.
                        const discuss = (questionsOnly || learners.length > 0) && (
                            <RecommendationCard
                              key="discuss"
                                        label="Discuss with learner"
                              status={statusFor(episode.id, 'discuss')}
                              actedBy={episode.statusBy?.discuss?.name}
                              onToggleDone={past ? undefined : () => setStatus(gap.id, episode.id, 'discuss', 'done')}
                              onToggleIgnored={past ? undefined : () => setStatus(gap.id, episode.id, 'discuss', 'ignored')}
                            >
                              <Typography sx={{ ...BODY }}>{discussWithLearnerCopy(learners)}</Typography>
                            </RecommendationCard>
                        );

                        // Only when the round is big enough to justify building a
                        // course: enough people failing, or 3+ questions.
                        const publish = course && (
                            <RecommendationCard
                              key="course"
                                        label="Build Training"
                              status={statusFor(episode.id, 'course')}
                              actedBy={episode.statusBy?.course?.name}
                              onToggleDone={past ? undefined : () => setStatus(gap.id, episode.id, 'course', 'done')}
                              onToggleIgnored={past ? undefined : () => setStatus(gap.id, episode.id, 'course', 'ignored')}
                                        ctaLabel="Review Course"
                              onCta={() => onPublishCourse(course)}
                            >
                              <CourseField label="Name" value={course.name} />
                              <CourseField
                                label="Audience"
                                value={course.audiences.join(', ')}
                                // Where the audience came from depends on how the
                                // gap was found — enrolment for a course,
                                // attributes for one built from conversations.
                                hint={
                                  gap.programName
                                    ? 'These are the audiences enrolled to the programs where these gaps were identified.'
                                    : 'These audiences align with the attributes of the employees who asked these questions.'
                                }
                              />
                              <CourseField label="Tone" value={course.tone.join(', ')} />
                              <CourseField label="Source Knowledge" value={course.documents.join(', ')} />
                              <CourseField label="Description" value={course.purpose} />
                              <Typography sx={{ ...BODY, color: '#868686', fontStyle: 'italic' }}>
                                          This program is ready for review on your Program Plan. It closes the gap for
                                          the
                                {questionsOnly
                                            ? ' learners who asked these questions.'
                                            : ' learners who have already completed the lesson.'}
                              </Typography>
                            </RecommendationCard>
                        );

                        const knowledge = (questionsOnly || unanswered) && (
                            <RecommendationCard
                              key="knowledge"
                              label="Add Knowledge"
                              status={statusFor(episode.id, 'knowledge')}
                              actedBy={episode.statusBy?.knowledge?.name}
                              onToggleDone={past ? undefined : () => setStatus(gap.id, episode.id, 'knowledge', 'done')}
                              onToggleIgnored={past ? undefined : () => setStatus(gap.id, episode.id, 'knowledge', 'ignored')}
                                        ctaLabel="Open Knowledge"
                              onCta={onOpenKnowledgeHub}
                            >
                              <Typography sx={{ ...BODY }}>{knowledgeRecommendationCopy(episode, gap.title)}</Typography>
                            </RecommendationCard>
                        );

                        // Only where Elliott judges an assessment is warranted —
                        // not every gap gets one, and never a question round.
                        const assessment = !questionsOnly && episode.suggestsAssessment && (
                            <RecommendationCard
                              key="assessment"
                              label="Run Skills Assessment"
                              // Not live yet, so there is nothing to mark done
                              // or ignore — and it must not block the gap.
                              locked
                                        {...(capabilityRelease
                                          ? {}
                                          : {
                                              ctaLabel: 'Run Skills Assessment',
                                              ctaDisabled: true,
                                              ctaTooltip: 'Coming soon'
                                            })}
                            >
                              <Typography sx={{ ...BODY, color: '#868686' }}>
                                          {capabilityRelease
                                            ? 'Build an Assessment in Capabilities to see whether this gap has closed.'
                                            : "I'd put a short skills assessment behind this to confirm the gap has closed. Coming soon."}
                              </Typography>
                            </RecommendationCard>
                        );

                        return (
                          <Box key={episode.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                            {/* No heading on the current episode — it is the
                                default view. Earlier episodes keep a date heading. */}
                            {past && (
                              <EpisodeHeading label="" since={episode.openedAt} current={false} first={i === 0} />
                            )}
                            {questionsOnly
                              ? [knowledge, publish, discuss]
                              : [updateLesson, discuss, publish, knowledge, assessment]}
                          </Box>
                        );
                      };
                      return (
                        <>
                          {current && renderSet(current, false, 0)}
                          {previous.length > 0 && (
                                        <PreviousGroup label="Previous recommendations" count={previous.length}>
                              {previous.map((episode, n) => renderSet(episode, true, n))}
                            </PreviousGroup>
                          )}
                        </>
                      );
                    })()}
                  </GapSection>
                </Box>
              </Collapse>
            </Box>
          );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })
        )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default GapsRisksPanel;
